// Third party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

// Components
import { ArrowNavigation, SecondaryLink, Button } from '../buttons';
import { HoldingIcon, QuickSearch } from '../common';
import { withErrorBoundary } from '../errors';
import { EditableInput, Checkbox } from '../inputs';
import { ErrorTooltip, InfoTooltip } from '../tooltips';
import { Pastille, GenericEmptyState } from '../misc';
import BMOGIFPortfolioDropdown from './BMOGIFPortfolioDropdown';
import EditBMOGIFPortfolioHolding from './EditBMOGIFPortfolioHolding';
import DiscardPortfolioHoldingsModal from '../modals/DiscardPortfolioHoldingsModal';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { getReadOnlyTooltipMessage, comparisonHasGifItems } from '../../../utils/comparison';
import { showError } from '../../../utils/errors';
import { GIF_PLAN_OPTIONS, GIF_CLASS_TYPES, GIF_PORTFOLIO_TYPES, BMOi18n, MIN_PRESTIGE_DEPOSIT, GIF_PORTFOLIO_COLOR } from '../../../utils/bmo-gif/constants';
import { getTotalGifPortfolioAllocation, validateBMOGIFPortfolio, validMaturityDate, isInvalidDepositAge } from '../../../utils/bmo-gif/index';
import { getAge } from '../../../utils/time';
import { COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import User from '@/types/user';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import {
  addFundToGifPortfolioById,
  removeHoldingFromGifPortfolioById,
  changeHoldingWeight,
  setPortfolioName,
  allocateRemaining,
  setPlanOption,
  setClassType,
  setPortfolioType,
  setDeathGuaranteeReset,
  setDeathGuaranteeResetEnhanced,
  clearGifPortfolioHoldings,
  setGifItemName,
  setPlanTerm,
  setBaseDob,
  calculateBMOPortfolio,
} from '../../../redux/modules/bmo-gif';
// Actions
import { removeGifItem } from '../../../redux/modules/comparison';
import { fetchBMOQuickSearchResults, QUICK_SEARCH_TYPE, QUICK_SEARCH_REDUCER_NAME } from '../../../redux/modules/quick-search';
import ComparisonContact from '@/types/comparison-contact';
import Comparison from '@/types/comparison';
import QuickSearchItem from '@/types/quick-search-item';
import BMOGIFPortfolioHolding from '@/types/bmo-gif/bmo-gif-portfolio-holding';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import { isPrestige } from '@/utils/bmo-gif/utils';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

type DispatchProps = {
  addFundToGifPortfolioById: (id: string) => Promise<void>;
  allocateRemaining: () => void;
  removeHoldingFromGifPortfolioById: (holding: BMOGIFPortfolioHolding) => void;
  changeHoldingWeight: (id: string, weight: number) => void;
  setPortfolioName: (name: string) => void;
  setPlanOption: (value: string, dob: Date) => void;
  setClassType: (value?: string) => void;
  setPortfolioType: (value?: string) => void;
  setDeathGuaranteeReset: (value: boolean) => void;
  setDeathGuaranteeResetEnhanced: (value: boolean) => void;
  fetchBMOQuickSearchResults: (query: string, reducerName: string, classType: string, planOption: string) => Promise<void>;
  clearGifPortfolioHoldings: () => void;
  setGifItemName: (name: string) => void;
  setPlanTerm: (value: number) => void;
  calculateBMOPortfolio: (autoSave?: boolean) => Promise<void>;
  setBaseDob: (contact: ComparisonContact) => void;
  onViewGraph: () => void;
  removeGifItem: () => void;
};

type StateProps = {
  portfolio: BMOGIFPortfolio;
  locale: string;
  readOnly?: boolean;
  comparison: Comparison;
  user: User;
};

type Props = DispatchProps & StateProps;

type State = {
  activeFundSearch: boolean;
  upArrowDisabled: boolean;
  downArrowDisabled: boolean;
  selectedClassType?: string;
  selectedPlanOption?: string;
  selectedPortfolioType?: string;
  showDiscardPortfolioHoldingsModal: boolean;
};

const VERTICAL_SCROLL_DISTANCE = 100;
const DEFAULT_MAX_HOLDINGS = 5;

class EditBMOGIFPortfolio extends Component<Props, State> {
  scrollBarRef: HTMLElement | undefined;

  readonly state: State = {
    activeFundSearch: this.props.portfolio.holdings.length === 0,
    upArrowDisabled: true,
    downArrowDisabled: true,
    showDiscardPortfolioHoldingsModal: false,
  };

  calculatePortfolio = debounce((autoSave?: boolean) => {
    this.props.calculateBMOPortfolio(autoSave);
  }, 300);

  componentDidUpdate(prevProps: Props) {
    const {
      portfolio,
      portfolio: {
        holdings,
        depositAmount,
        subsequentDepositAmount,
        start,
        end,
        frequency,
        salesChargeType,
        salesChargeAmount,
        projectionBasis,
        purchaseDate,
        term,
        deathGuaranteeReset,
        deathGuaranteeResetEnhanced,
        planOption,
        portfolioType,
        classType,
        dob,
        isDefaultState,
        customRate,
      },
      comparison,
      readOnly,
    } = this.props;

    const newTotalAllocation = getTotalGifPortfolioAllocation(portfolio);
    const oldTotalAllocation = getTotalGifPortfolioAllocation(prevProps.portfolio);

    if (holdings.length !== prevProps.portfolio.holdings.length) {
      if (this.scrollBarRef && holdings.length > this.getMaxHoldings() && holdings.length >= prevProps.portfolio.holdings.length) {
        this.scrollBarRef.scrollTop = VERTICAL_SCROLL_DISTANCE * (holdings.length - this.getMaxHoldings());
      }
      this.portfolioHoldingsOverflow();
    }

    if (newTotalAllocation !== 100) {
      // Don't calculate portfolio stats if not fully allocated.
      return;
    }
    const allocationHasChanged = newTotalAllocation !== oldTotalAllocation;

    if (prevProps.portfolio.isDefaultState && isDefaultState && validateBMOGIFPortfolio(portfolio)) {
      this.calculatePortfolio();
    }

    if (
      !prevProps.portfolio.isDefaultState &&
      (allocationHasChanged ||
        depositAmount !== prevProps.portfolio.depositAmount ||
        subsequentDepositAmount !== prevProps.portfolio.subsequentDepositAmount ||
        start !== prevProps.portfolio.start ||
        end !== prevProps.portfolio.end ||
        frequency !== prevProps.portfolio.frequency ||
        salesChargeType !== prevProps.portfolio.salesChargeType ||
        salesChargeAmount !== prevProps.portfolio.salesChargeAmount ||
        term !== prevProps.portfolio.term ||
        deathGuaranteeReset !== prevProps.portfolio.deathGuaranteeReset ||
        deathGuaranteeResetEnhanced !== prevProps.portfolio.deathGuaranteeResetEnhanced ||
        projectionBasis !== prevProps.portfolio.projectionBasis ||
        purchaseDate !== prevProps.portfolio.purchaseDate ||
        classType !== prevProps.portfolio.classType ||
        planOption !== prevProps.portfolio.planOption ||
        portfolioType !== prevProps.portfolio.portfolioType ||
        customRate !== prevProps.portfolio.customRate ||
        dob !== prevProps.portfolio.dob)
    ) {
      const inValidAge = comparison.contact && isInvalidDepositAge(portfolio, comparison.contact);
      if (validateBMOGIFPortfolio(portfolio) && !readOnly && inValidAge && !inValidAge.isInvalid) {
        const autoSave = comparisonHasGifItems(comparison) && dob !== prevProps.portfolio.dob;
        this.calculatePortfolio(autoSave);
      }
      if (inValidAge && inValidAge.isInvalid) {
        this.props.removeGifItem();
      }
    }

    const { contact } = comparison;

    if (!contact || !prevProps.comparison.contact) {
      return;
    }

    if (purchaseDate !== prevProps.portfolio.purchaseDate || contact.dob !== prevProps.comparison.contact.dob) {
      this.props.setBaseDob(contact);
    }
  }

  onScrollUp = throttle(() => {
    if (this.state.downArrowDisabled) {
      this.setState({ downArrowDisabled: false });
    }
  }, 100);

  onScrollDown = throttle(() => {
    if (this.state.upArrowDisabled) {
      this.setState({ upArrowDisabled: false });
    }
  }, 100);

  getMaxHoldings = () => {
    const {
      readOnly,
      portfolio: { planOption, classType, portfolioType },
    } = this.props;

    const hasSearch = !readOnly && planOption && classType && portfolioType === GIF_PORTFOLIO_TYPES.CUSTOM;

    return hasSearch ? DEFAULT_MAX_HOLDINGS : DEFAULT_MAX_HOLDINGS + 1;
  };

  portfolioHoldingsOverflow = () => {
    const {
      portfolio: { holdings },
    } = this.props;

    if (!holdings.length) {
      return;
    }

    if (holdings.length <= this.getMaxHoldings()) {
      this.setState({ downArrowDisabled: true, upArrowDisabled: true });
    } else {
      this.setState({ downArrowDisabled: false });
    }
  };

  portfolioIsFull = (): boolean => {
    const { portfolio } = this.props;

    return getTotalGifPortfolioAllocation(portfolio) === 100;
  };

  onAllocateRemaining = () => {
    this.props.allocateRemaining();
  };

  renderFunds = () => {
    const {
      portfolio: { holdings, planOption, classType, portfolioType },
      readOnly,
      locale,
      changeHoldingWeight,
      removeHoldingFromGifPortfolioById,
    } = this.props;

    if (!planOption || !classType || !portfolioType) {
      return <GenericEmptyState header={BMOi18n[locale].INCOMPLETE_PORTFOLIO_TITLE} body={BMOi18n[locale].INCOMPLETE_PORTFOLIO_BODY} />;
    }

    if (holdings.length > 0) {
      return (
        <>
          <div className="weight">
            <FormattedMessage id="HoldingsEditor.Weight" />
          </div>

          <PerfectScrollbar
            className="bmo-gif-portfolio-holdings-container-v2"
            onYReachStart={this.onYReachStart}
            onYReachEnd={this.onYReachEnd}
            onScrollDown={this.onScrollDown}
            onScrollUp={this.onScrollUp}
            containerRef={ref => {
              this.scrollBarRef = ref;
            }}
          >
            <div className="bmo-gif-portfolio-holdings-v2">
              {holdings.map(holding => (
                <EditBMOGIFPortfolioHolding
                  key={holding.id}
                  portfolioHolding={holding}
                  onRemoveHolding={removeHoldingFromGifPortfolioById}
                  onWeightChange={changeHoldingWeight}
                  readOnly={readOnly}
                  customTemplate={portfolioType === GIF_PORTFOLIO_TYPES.CUSTOM}
                />
              ))}
            </div>
          </PerfectScrollbar>
        </>
      );
    }

    return (
      <div className="bmo-gif-portfolio-no-funds-v2">
        <span>{BMOi18n[locale].EMPTY_PORTFOLIO}</span>
      </div>
    );
  };

  toggleAddFund = () => {
    this.setState(state => ({
      activeFundSearch: !state.activeFundSearch,
    }));
  };

  onAddItem = (item: QuickSearchItem) => {
    switch (item.type) {
      case COMPARISON_ITEM_TYPES.FUND: {
        return this.props.addFundToGifPortfolioById(item._id).catch(showError);
      }
      default: {
        return Promise.reject(new Error('Unsupported search item type'));
      }
    }
  };

  searchBMOFunds = (query: string, reducerName: string): Promise<void> => {
    const {
      portfolio: { classType, planOption },
    } = this.props;
    if (!classType || !planOption) {
      return Promise.reject();
    }

    return this.props.fetchBMOQuickSearchResults(query, reducerName, classType, planOption);
  };

  onArrowScroll = (distance: number) => {
    const scrollbarContainer = document.getElementsByClassName('scrollbar-container bmo-gif-portfolio-holdings-container-v2 ps')[0] as HTMLDivElement;
    scrollbarContainer.scrollBy({ top: distance });
  };

  onYReachStart = () => {
    if (!this.state.upArrowDisabled) {
      this.setState({ upArrowDisabled: true });
    }
  };

  onYReachEnd = () => {
    if (!this.state.downArrowDisabled) {
      this.setState({ downArrowDisabled: true });
    }
  };

  isInvalidPlanAndType = (value: string) => {
    const {
      portfolio: { portfolioType },
    } = this.props;

    return (
      value === GIF_PLAN_OPTIONS.GIF75100PLUS && (portfolioType === GIF_PORTFOLIO_TYPES.FORTY_SIXTY || portfolioType === GIF_PORTFOLIO_TYPES.TWENTY_EIGHTY)
    );
  };

  handleChangePlanOption = (value: string) => {
    const {
      portfolio: { holdings, portfolioType, classType },
    } = this.props;

    let resetClassType = false;
    const newState: Partial<State> = { selectedPlanOption: value };
    if (value !== GIF_PLAN_OPTIONS.GIF7575 && classType === GIF_CLASS_TYPES.CLASS_F_PRESTIGE) {
      newState.selectedClassType = undefined;
      resetClassType = true;
    }

    this.setState({ ...newState } as Pick<State, keyof State>, () => {
      if (holdings.length && portfolioType === GIF_PORTFOLIO_TYPES.CUSTOM) {
        this.showDiscardPortfolioHoldingsModal();
      } else {
        this.onChangePlanOption();
        if (resetClassType) {
          this.props.setClassType(undefined);
        }
      }
    });

    if (value !== GIF_PLAN_OPTIONS.GIF100100) {
      this.props.setDeathGuaranteeReset(false);
    }
  };

  onChangePlanOption = () => {
    const { selectedPlanOption } = this.state;
    const {
      comparison: { contact },
    } = this.props;

    if (!selectedPlanOption || !contact || !contact.dob) {
      return;
    }

    if (this.isInvalidPlanAndType(selectedPlanOption)) {
      this.props.setPortfolioType(undefined);
      this.props.clearGifPortfolioHoldings();
    }
    this.props.setPlanOption(selectedPlanOption, contact.dob);
    this.setState({ selectedPlanOption: undefined });
  };

  showDiscardPortfolioHoldingsModal = () => this.setState({ showDiscardPortfolioHoldingsModal: true });

  hideDiscardPortfolioHoldingsModal = () => {
    this.setState({
      showDiscardPortfolioHoldingsModal: false,
      selectedPlanOption: undefined,
      selectedPortfolioType: undefined,
      selectedClassType: undefined,
    });
  };

  handleChangeClassType = (value: string) => {
    const {
      portfolio: { holdings, portfolioType },
    } = this.props;

    this.setState({ selectedClassType: value }, () => {
      if (holdings.length && portfolioType === GIF_PORTFOLIO_TYPES.CUSTOM) {
        this.showDiscardPortfolioHoldingsModal();
      } else {
        this.onChangeClassType();
      }
    });
  };

  onChangeClassType = () => {
    const { selectedClassType } = this.state;

    if (!selectedClassType) {
      return;
    }

    this.props.setClassType(selectedClassType);
    this.setState({ selectedClassType: undefined });
  };

  onConfirmDiscardPortfolioHoldings = () => {
    this.props.clearGifPortfolioHoldings();
    this.onChangeClassType();
    this.onChangePlanOption();
    this.onChangePortfolioType();
    this.hideDiscardPortfolioHoldingsModal();
  };

  handleChangePortfolioType = (value: string) => {
    const {
      portfolio: { holdings, portfolioType },
    } = this.props;

    this.setState({ selectedPortfolioType: value }, () => {
      if (holdings.length && portfolioType === GIF_PORTFOLIO_TYPES.CUSTOM && value !== GIF_PORTFOLIO_TYPES.CUSTOM) {
        this.showDiscardPortfolioHoldingsModal();
      } else {
        if (value === GIF_PORTFOLIO_TYPES.CUSTOM) {
          this.props.clearGifPortfolioHoldings();
        }

        this.onChangePortfolioType();
      }
    });
  };

  onChangePortfolioType = () => {
    const { selectedPortfolioType } = this.state;

    if (!selectedPortfolioType) {
      return;
    }

    this.props.setPortfolioType(selectedPortfolioType);
    this.setState({ selectedPortfolioType: undefined });
  };

  onChangeTerm = (value: number) => {
    this.props.setPlanTerm(value);
  };

  toggleDeathGuaranteeReset = () => {
    const {
      portfolio: { deathGuaranteeReset },
    } = this.props;

    this.props.setDeathGuaranteeReset(!deathGuaranteeReset);
  };

  toggleDeathGuaranteeResetEnhanced = () => {
    const {
      portfolio: { deathGuaranteeResetEnhanced },
    } = this.props;

    this.props.setDeathGuaranteeResetEnhanced(!deathGuaranteeResetEnhanced);
  };

  renderDropdowns = () => {
    const {
      portfolio: {
        planOption,
        classType,
        portfolioType,
        purchaseDate,
        deathGuaranteeReset,
        deathGuaranteeResetEnhanced,
        term,
        depositAmount,
        contractMaturityDate,
        dob,
      },
      locale,
      readOnly,
      comparison,
      user,
    } = this.props;

    const baseAge = dob && getAge(dob, undefined, purchaseDate);

    const planOptionValues = Object.keys(GIF_PLAN_OPTIONS).map(option => ({
      value: GIF_PLAN_OPTIONS[option],
      label: BMOi18n[locale][option],
    }));

    let classTypes = Object.keys(GIF_CLASS_TYPES);

    if (planOption !== GIF_PLAN_OPTIONS.GIF7575) {
      classTypes = classTypes.filter(classType => GIF_CLASS_TYPES[classType] !== GIF_CLASS_TYPES.CLASS_F_PRESTIGE);
    }

    const classTypeValues = classTypes.map(option => ({
      value: GIF_CLASS_TYPES[option],
      label: BMOi18n[locale][option],
    }));

    const filterPortfolioTypes = (option: { value: string; label: string }) => {
      if (planOption === GIF_PLAN_OPTIONS.GIF75100PLUS) {
        return option.value !== GIF_PORTFOLIO_TYPES.TWENTY_EIGHTY && option.value !== GIF_PORTFOLIO_TYPES.FIFTY_FIFTY;
      }
      if (planOption === GIF_PLAN_OPTIONS.GIF100100) {
        return option.value !== GIF_PORTFOLIO_TYPES.EIGHTY_TWENTY && option.value !== GIF_PORTFOLIO_TYPES.TWENTY_EIGHTY;
      }

      return true;
    };

    const portfolioTypesValues = Object.keys(GIF_PORTFOLIO_TYPES)
      .map(option => ({
        value: GIF_PORTFOLIO_TYPES[option],
        label: BMOi18n[locale][option],
      }))
      .filter(filterPortfolioTypes);

    const termValues = [];

    for (let i = 15; i <= 25; i++) {
      termValues.push({ value: i, label: i.toString() });
    }

    const planOptionsDisabled = !!readOnly;
    const classTypesDisabled = !!readOnly || !planOption;
    const portfolioTypesDisabled = !!readOnly || !planOption || !classType;
    const showPrestigeWarning = isPrestige(classType) && (depositAmount === undefined || depositAmount < MIN_PRESTIGE_DEPOSIT);

    const planOptionTooltip = getReadOnlyTooltipMessage(comparison);
    const classTypeTooltip = readOnly ? getReadOnlyTooltipMessage(comparison) : BMOi18n[locale].CLASS_TYPE_DISABLED;
    const portfolioTypeTooltip = readOnly ? getReadOnlyTooltipMessage(comparison) : BMOi18n[locale].PORTFOLIO_TYPE_DISABLED;

    // Temporary feature flag for release
    const userCanSelectDGRE = userHasPermission(user, PERMISSIONS.BMO_GIF_DGRE);

    return (
      <div className="d-flex flex-column flex-fill">
        <div className="plan-options-container d-flex align-items-center">
          <BMOGIFPortfolioDropdown
            className="plan-options"
            label={BMOi18n[locale].PLAN_OPTIONS}
            selected={planOption}
            onChange={this.handleChangePlanOption}
            options={planOptionValues}
            disabled={planOptionsDisabled}
            tooltipMessage={planOptionTooltip}
            tooltipDisabled={!planOptionsDisabled}
            horizontalLabel
          />

          {userCanSelectDGRE && planOption === GIF_PLAN_OPTIONS.GIF75100 && (
            <div className="d-flex">
              <div className="death-guarantee-resets">
                <Checkbox
                  ariaLabel={BMOi18n[locale].DEATHGUARANTEEENHANCED}
                  selected={deathGuaranteeResetEnhanced}
                  onChange={this.toggleDeathGuaranteeResetEnhanced}
                  disabled={planOptionsDisabled}
                />
                <span className="">
                  {BMOi18n[locale].DEATHGUARANTEEENHANCED}
                  <InfoTooltip message={BMOi18n[locale].DEATHGUARANTEEENHANCEDINFO} />
                </span>
              </div>
            </div>
          )}

          {planOption === GIF_PLAN_OPTIONS.GIF75100PLUS && <InfoTooltip message={BMOi18n[locale].GIF75100PLUSINFO} />}

          {planOption === GIF_PLAN_OPTIONS.GIF100100 && (
            <div className="d-flex">
              <div className="term d-flex align-items-center">
                {!validMaturityDate(planOption, purchaseDate, baseAge, contractMaturityDate, term) && <ErrorTooltip message={BMOi18n[locale].INVALID_TERM} />}

                <BMOGIFPortfolioDropdown
                  label={BMOi18n[locale].TERM}
                  selected={term}
                  onChange={this.onChangeTerm}
                  options={termValues}
                  disabled={planOptionsDisabled}
                  tooltipMessage={planOptionTooltip}
                  tooltipDisabled={!planOptionsDisabled}
                  horizontalLabel
                />
              </div>

              <div className="death-guarantee-resets">
                <Checkbox
                  ariaLabel={BMOi18n[locale].DEATHGUARANTEE}
                  selected={deathGuaranteeReset}
                  onChange={this.toggleDeathGuaranteeReset}
                  disabled={planOptionsDisabled}
                />
                <span>
                  {BMOi18n[locale].DEATHGUARANTEE}
                  <InfoTooltip message={BMOi18n[locale].DEATHGUARANTEEINFO} />
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center">
          <BMOGIFPortfolioDropdown
            className="class-types"
            label={BMOi18n[locale].CLASS_TYPE}
            selected={classType}
            onChange={this.handleChangeClassType}
            options={classTypeValues}
            disabled={classTypesDisabled}
            tooltipMessage={classTypeTooltip}
            tooltipDisabled={!classTypesDisabled}
            showWarning={showPrestigeWarning}
            warningMessage={BMOi18n[locale].PRESTIGE_INFO}
            horizontalLabel
          />

          <BMOGIFPortfolioDropdown
            className="portfolio-types"
            label={BMOi18n[locale].PORTFOLIO_TYPE}
            selected={portfolioType}
            onChange={this.handleChangePortfolioType}
            options={portfolioTypesValues}
            disabled={portfolioTypesDisabled}
            tooltipMessage={portfolioTypeTooltip}
            tooltipDisabled={!portfolioTypesDisabled}
            horizontalLabel
          />
        </div>
      </div>
    );
  };

  renderQuickSearch = () => {
    const {
      portfolio: { planOption, classType, portfolioType },
      readOnly,
      locale,
    } = this.props;
    const { activeFundSearch } = this.state;

    if (readOnly || !planOption || !classType || portfolioType !== GIF_PORTFOLIO_TYPES.CUSTOM) {
      return null;
    }

    return (
      <div className="edit-bmo-gif-portfolio-add-funds">
        {!activeFundSearch ? (
          <QuickSearch
            targetReducer={QUICK_SEARCH_REDUCER_NAME.EDIT_BMO_PORTFOLIO}
            allowedTypes={[QUICK_SEARCH_TYPE.FUNDS]}
            className="edit-bmo-gif-portfolio-quick-search"
            onItemSelection={(item: QuickSearchItem) => this.onAddItem(item)}
            customSearch={this.searchBMOFunds}
            customPlaceholder={BMOi18n[locale].QUICK_SEACH_PLACEHOLDER}
          />
        ) : (
          <SecondaryLink onClick={this.toggleAddFund}>
            <i className="icon-plus" />
            <span>{BMOi18n[locale].ADD_HOLDING}</span>
          </SecondaryLink>
        )}
      </div>
    );
  };

  updateGifItemName = () => {
    this.props.setGifItemName(this.props.portfolio.name);
  };

  viewGraphDisabled = () => {
    const { comparison, portfolio } = this.props;

    const portfolioIsFull = getTotalGifPortfolioAllocation(portfolio) === 100;

    return !comparison.gifItems || !comparison.gifItems.length || !comparison.gifItems[0].baseScenario || !portfolioIsFull;
  };

  render() {
    const { portfolio, readOnly, locale, setPortfolioName, onViewGraph } = this.props;
    const { upArrowDisabled, downArrowDisabled, showDiscardPortfolioHoldingsModal } = this.state;
    const totalAllocated = getTotalGifPortfolioAllocation(portfolio);

    const hasSearch = !readOnly && portfolio.planOption && portfolio.classType && portfolio.portfolioType === GIF_PORTFOLIO_TYPES.CUSTOM;

    return (
      <div className="edit-bmo-gif-portfolio-v2">
        <div className="edit-bmo-gif-portfolio-header">
          <div className="top-row d-flex justify-content-between">
            <div className="edit-bmo-gif-portfolio-infos">
              <div className="edit-bmo-gif-portfolio-name d-flex align-items-center">
                <HoldingIcon className="holding-icon" type={COMPARISON_ITEM_TYPES.PORTFOLIO} color={GIF_PORTFOLIO_COLOR} />
                {!portfolio.name && <ErrorTooltip message={formatMessage('HoldingsEditor.NameDisclaimer.PORTFOLIO')} />}

                <EditableInput
                  compact
                  data-private
                  data-dd-privacy="mask"
                  className={!readOnly ? 'flex-fill' : ''}
                  value={portfolio.name}
                  placeholder={formatMessage('HoldingsEditor.NamePlaceholder.PORTFOLIO')}
                  onValueChange={setPortfolioName}
                  defaultToEdit={!portfolio.name}
                  onBlur={this.updateGifItemName}
                  readOnly={readOnly}
                />

                {readOnly && (
                  <Pastille className="pastille-read-only">
                    <FormattedMessage id="Common.ReadOnly" />
                  </Pastille>
                )}
              </div>
            </div>

            <div className="edit-bmo-gif-portfolio-actions d-flex align-items-center">
              <Button disabled={this.viewGraphDisabled()} onClick={onViewGraph}>
                <i className="icon-chart-line" />
                <span>{BMOi18n[locale].VIEW_GRAPH}</span>
              </Button>
            </div>
          </div>

          <div className="bottom-row d-flex align-items-center">{this.renderDropdowns()}</div>
        </div>

        <div className={classNames('edit-bmo-gif-portfolio-holdings-container-v2', { 'no-search': !hasSearch })}>
          <ArrowNavigation direction="up" className={classNames({ disabled: upArrowDisabled })} onClick={() => this.onArrowScroll(-VERTICAL_SCROLL_DISTANCE)} />

          {this.renderFunds()}

          <ArrowNavigation
            direction="down"
            className={classNames({ disabled: downArrowDisabled })}
            onClick={() => this.onArrowScroll(VERTICAL_SCROLL_DISTANCE)}
          />
        </div>

        {this.renderQuickSearch()}

        <div className="d-flex align-items-center">
          {!readOnly && (
            <div className="edit-bmo-gif-portfolio-allocation">
              <div className="d-flex allocate-remaining-container">
                <div className="allocate-remaining flex-grow-1">
                  {getTotalGifPortfolioAllocation(portfolio) < 100 && portfolio.holdings.length > 0 && (
                    <SecondaryLink onClick={this.onAllocateRemaining}>
                      <FormattedMessage id="HoldingsEditor.AllocateRemaining" />
                    </SecondaryLink>
                  )}
                </div>

                <FormattedMessage id="HoldingsEditor.TotalAllocated" />

                <span className="total-allocated-value">{`${totalAllocated}%`}</span>

                {totalAllocated > 100 && <ErrorTooltip message={formatMessage('HoldingsEditor.OverAllocation.PORTFOLIO')} />}

                {totalAllocated < 100 && <ErrorTooltip message={formatMessage('HoldingsEditor.UnderAllocation.PORTFOLIO')} />}
              </div>
            </div>
          )}
        </div>

        <DiscardPortfolioHoldingsModal
          show={showDiscardPortfolioHoldingsModal}
          onConfirm={this.onConfirmDiscardPortfolioHoldings}
          onCancel={this.hideDiscardPortfolioHoldingsModal}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ bmoGif, intl, auth }: ReduxState) => ({
  portfolio: bmoGif.portfolio,
  locale: intl.locale,
  user: auth.user,
});

export default connect(mapStateToProps, {
  addFundToGifPortfolioById,
  allocateRemaining,
  removeHoldingFromGifPortfolioById,
  changeHoldingWeight,
  setPortfolioName,
  setPlanOption,
  setClassType,
  setPortfolioType,
  setDeathGuaranteeReset,
  setDeathGuaranteeResetEnhanced,
  fetchBMOQuickSearchResults,
  clearGifPortfolioHoldings,
  setGifItemName,
  setPlanTerm,
  calculateBMOPortfolio,
  setBaseDob,
  removeGifItem,
})(withErrorBoundary(EditBMOGIFPortfolio));
